import { ApiErrorResponse, isApiError } from "@common/api";
import Alert, { TicketColor } from "@common/components/Alert";
import {
  AlertColor,
  Box,
  Portal,
  Slide,
  Snackbar,
  SnackbarOrigin,
  Stack,
} from "@mui/material";
import axios from "axios";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import DialogAlert from "../DialogAlert";
import {
  PopupLocationKeys,
  useUserSetting,
} from "../ProfileBadge/DrawerContent/SettingsContent";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";

export type Notification = {
  key?: number;
  title: string;
  message: string;
  anchorOrigin?: SnackbarOrigin;
  duration?: number;
  severity?: AlertColor;
  details?: ApiErrorResponse;
  toast?: boolean;
  priority?: TicketColor;
  closeButtonLabel?: string;
  redirectTo?: string;
  icon?: React.JSX.Element;
  cancelOthers?: boolean;
  silent?: boolean;
  clearFunction?: (id: number) => void;
  notif?: boolean;
  notificationId?: number;
};
type SlideDirection = "left" | "right" | "up" | "down";
const MAX_STACKED_NOTIFICATIONS = 3;
const SLIDE_ANIMATION_TIME_MS = 300;
const MAX_DURATION = 10000;

const popupLocationMapping: Record<
  PopupLocationKeys,
  SnackbarOrigin & { transitionDirection: SlideDirection }
> = {
  "Bottom Center": {
    vertical: "bottom",
    horizontal: "center",
    transitionDirection: "up",
  },
  "Bottom Left Corner": {
    vertical: "bottom",
    horizontal: "left",
    transitionDirection: "right",
  },
  "Bottom Right Corner": {
    vertical: "bottom",
    horizontal: "right",
    transitionDirection: "left",
  },
  "Top Center": {
    vertical: "top",
    horizontal: "center",
    transitionDirection: "down",
  },
  "Top Left Corner": {
    vertical: "top",
    horizontal: "left",
    transitionDirection: "right",
  },
  "Top Right Corner": {
    vertical: "top",
    horizontal: "right",
    transitionDirection: "left",
  },
};

const StackedNotification = ({
  notificationId,
  title,
  severity = "error",
  details,
  priority,
  redirectTo,
  icon,
  message,
  duration = MAX_DURATION,
  delay,
  index,
  direction,
}: Notification & {
  delay: number;
  index: number;
  direction: SlideDirection;
}) => {
  const [open, setOpen] = useState(true);
  const { removeNotification } = useContext(NotificationContext);

  const closeNotification = useCallback(() => {
    if (!notificationId) return;
    setOpen(false);
    setTimeout(() => {
      removeNotification(notificationId);
    }, SLIDE_ANIMATION_TIME_MS);
  }, [notificationId, removeNotification]);

  useEffect(() => {
    if (!open || !notificationId) return;
    if (duration !== 0) {
      const timer = setTimeout(() => {
        closeNotification();
      }, delay || MAX_DURATION);
      return () => clearTimeout(timer);
    }
    return;
  }, [closeNotification, duration, open]);

  return (
    <Slide
      in={open}
      direction={direction}
      timeout={SLIDE_ANIMATION_TIME_MS}
      style={{ transitionDelay: `${index}00ms` }}
      key={notificationId}
    >
      <Alert
        key={notificationId}
        title={title}
        severity={severity}
        details={details}
        priority={priority}
        redirectTo={redirectTo}
        icon={icon}
        notif={true}
        clearFunction={closeNotification}
        alarmId={notificationId}
      >
        {message}
      </Alert>
    </Slide>
  );
};

const NotificationFC = () => {
  const { notifications, dispatchNotification } =
    useContext(NotificationContext);
  const [showDetails, setShowDetails] = useState(false);
  const [hideTicketPopup] = useUserSetting("hideTicketPopup");
  const [popupLocation] = useUserSetting("popupLocation");
  const [ticketFilterByPriority] = useUserSetting("ticketFilterByPriority");

  const { horizontal, vertical, transitionDirection } =
    popupLocationMapping[popupLocation];

  const [simpleNotification] = notifications.filter((n) => !n.notificationId);
  const stackedNotifications = notifications
    .filter(
      (n) =>
        n.notificationId &&
        (ticketFilterByPriority.length === 0 ||
          ticketFilterByPriority.some(
            (priority) =>
              n.priority && n.priority.toLowerCase() === priority.toLowerCase()
          ))
    )
    .slice(0, MAX_STACKED_NOTIFICATIONS)
    .reverse();

  const handleCloseDialog = () => {
    dispatchNotification({ type: "pop" });
    setShowDetails(false);
  };

  const calculateDelay = (index: number) => {
    return (
      (stackedNotifications.length - index) *
      (notifications[index]?.duration ?? MAX_DURATION)
    );
  };

  const shouldRemoveNotification = !(
    horizontal === "left" &&
    vertical === "bottom" &&
    simpleNotification
  );

  useEffect(() => {
    if (!shouldRemoveNotification) {
      dispatchNotification({ type: "removeAllTicket" });
    }
  }, [shouldRemoveNotification]);

  return (
    <>
      <Portal>
        {stackedNotifications.length > 0 &&
          !hideTicketPopup &&
          shouldRemoveNotification && (
            <Snackbar
              open
              autoHideDuration={null}
              anchorOrigin={{ horizontal, vertical }}
              sx={{
                maxWidth: "30rem",
                minWidth: "25rem",
                zIndex: 1100,
              }}
            >
              <Box>
                <Stack
                  direction="column"
                  sx={{
                    gap: 1,
                  }}
                >
                  {stackedNotifications.map((notification, index) => (
                    <StackedNotification
                      index={stackedNotifications.length - index}
                      delay={calculateDelay(index)}
                      key={notification.notificationId ?? index}
                      direction={transitionDirection}
                      {...notification}
                    />
                  ))}
                </Stack>
              </Box>
            </Snackbar>
          )}

        {simpleNotification && !showDetails && (
          <Snackbar
            open
            autoHideDuration={simpleNotification.duration ?? 10000}
            anchorOrigin={
              simpleNotification.anchorOrigin ?? {
                horizontal: "left",
                vertical: "bottom",
              }
            }
            onClose={() => {
              if (showDetails === false) handleCloseDialog();
            }}
            sx={{ maxWidth: "30rem", minWidth: "25rem" }}
          >
            <Box>
              <Alert
                title={simpleNotification.title}
                severity={simpleNotification.severity ?? "error"}
                details={simpleNotification.details}
                showDetails={setShowDetails}
                priority={simpleNotification.priority}
                redirectTo={simpleNotification.redirectTo}
                icon={simpleNotification.icon}
              >
                {simpleNotification.message}
              </Alert>
            </Box>
          </Snackbar>
        )}
      </Portal>
      {showDetails && simpleNotification && (
        <DialogAlert
          severity={simpleNotification.severity ?? "error"}
          open
          title={simpleNotification.title}
          message={simpleNotification.message}
          onClose={handleCloseDialog}
          details={simpleNotification.details}
          closeButtonLabel={simpleNotification.closeButtonLabel}
        />
      )}
    </>
  );
};

type WithAction<T extends string, D = undefined> = { type: T; value?: D };

export type ActionNotification =
  | WithAction<"pop">
  | WithAction<"remove", Notification["key"]>
  | WithAction<"removeAllTicket", Notification[]>
  | Notification;

export interface INotificationContext {
  notifications: Notification[];
  dispatchNotification: (action: ActionNotification) => void;
  removeNotification: (key: Notification["key"]) => void;
}

export const NotificationContext = createContext<INotificationContext>({
  notifications: [],
  dispatchNotification: () => {},
  removeNotification: () => {},
});

const NotificationProvider = (props: React.PropsWithChildren<{}>) => {
  const [user] = useAuthenticatedUser();
  const removeNotification = (id: Notification["key"]) => {
    dispatchNotification({ type: "remove", value: id });
  };
  const isWithAction = (x: ActionNotification): x is WithAction<any, any> =>
    (x as WithAction<any, any>).type !== undefined;

  const reducer = (
    state: INotificationContext,
    action: ActionNotification
  ): INotificationContext => {
    if (isWithAction(action)) {
      if (action.type === "pop") {
        let { notifications, ticketNotifications } = state.notifications.reduce(
          (acc, notification) => {
            if (!notification.notificationId) {
              acc.notifications.push(notification);
            } else {
              acc.ticketNotifications.push(notification);
            }
            return acc;
          },
          {
            notifications: [] as Notification[],
            ticketNotifications: [] as Notification[],
          }
        );

        let notification = notifications.shift();
        if (notification?.cancelOthers) {
          notifications = [];
        }
        return {
          ...state,
          notifications: [...notifications, ...ticketNotifications],
        };
      }

      if (action.type === "remove") {
        const updatedNotifications = [...state.notifications].filter(
          (notification) => notification.notificationId !== action.value
        );
        return {
          ...state,
          notifications: updatedNotifications,
        };
      }
      if (action.type === "removeAllTicket") {
        const deletedNotifications = [...state.notifications].filter(
          (notification) => !notification.notificationId
        );
        return {
          ...state,
          notifications: deletedNotifications,
        };
      }
      return state;
    } else {
      let notifications = [...state.notifications];
      if (!action.silent) notifications.push({ ...action });
      return {
        ...state,
        notifications: notifications,
      };
    }
  };

  const [state, dispatchNotification] = useReducer(reducer, {
    notifications: [],
    dispatchNotification: () => {},
    removeNotification: () => {},
  });

  useEffect(() => {
    // Remove tickets from the notification queue on logout as per SF-4181
    if (Boolean(user) === false) {
      dispatchNotification({ type: "removeAllTicket" });
    }
  }, [user]);

  return (
    <NotificationContext.Provider
      value={{ ...state, dispatchNotification, removeNotification }}
    >
      <NotificationFC />
      {props.children}
    </NotificationContext.Provider>
  );
};

export const useErrorNotification = () => {
  const { dispatchNotification } = useContext(NotificationContext);
  const { t, i18n } = useTranslation();
  const title = t("Error");

  const dispatchErrorNotification = (
    e: any,
    options: Omit<Notification, "title" | "message"> & {
      withDetails?: boolean;
      displayMessage?: boolean;
      resourceName?: string;
    } = {}
  ) => {
    const {
      withDetails = true,
      displayMessage = false,
      resourceName,
      ...otherOptions
    } = options;
    let message;
    let details: ApiErrorResponse | undefined;
    let cancelOthers = false;
    if (isApiError(e)) {
      if (displayMessage) {
        message = i18n.exists(e.response.data.message)
          ? t(e.response.data.message)
          : e.response.data.message;
      } else {
        if (i18n.exists(e.response.data.errorCode)) {
          message = t(e.response.data.errorCode);
        } else if (i18n.exists(e.response.data.error)) {
          message = t(e.response.data.error);
        }
        switch (e.response.data.errorCode) {
          case "license_violation_rooms_amount":
            message = t(
              "Current number of rooms is greater than the license allows {{ actual_amount }} > {{ allowed_amount }}",
              {
                actual_amount: e.response.data.errorContext.actual_amount ?? 0,
                allowed_amount:
                  e.response.data.errorContext.allowed_amount ?? 0,
              }
            );
            break;
          case "license_violation_devices_amount":
            message = t(
              "Current number of devices is greater than the license allows {{ actual_amount }} > {{ allowed_amount }}",
              {
                actual_amount: e.response.data.errorContext.actual_amount ?? 0,
                allowed_amount:
                  e.response.data.errorContext.allowed_amount ?? 0,
              }
            );
            break;
          case "license_violation_device_amount":
            message = t(
              "Current license doesn't allow to have devices more than: {{ allowed_amount }}",
              { allowed_amount: e.response.data.errorContext.allowed_amount }
            );
            break;
          case "invalid_state":
            if (i18n.exists(e.response.data.error))
              message = t(e.response.data.error);
            break;

          case "not_found": {
            if (resourceName) {
              message = t("{{ resourceName }} not found", {
                resourceName,
              });
            } else {
              message = t("not_found");
            }
            break;
          }
          default:
            break;
        }

        if (message == "") {
          switch (e.response.data.errorContext?.constraint) {
            case "NOT_SUPPORTED_VALUE":
              if (e.response.data.errorContext?.invalidValue === "") {
                message = t('The field: "{{field}}" cannot be null.', {
                  field: e.response.data.errorContext?.invalidArgument,
                });
              } else if (Boolean(!e.response.data.errorContext?.invalidValue)) {
                message = t('"The field: "{{field}}" is invalid"', {
                  field: e.response.data.errorContext?.invalidArgument,
                });
              } else {
                message = t(
                  'The value: "{{value}}" is not allowed for the following field: {{field}}.',
                  {
                    value: e.response.data.errorContext?.invalidValue,
                    field: e.response.data.errorContext?.invalidArgument,
                  }
                );
              }

              break;
            default:
              break;
          }
        }
      }
      if (withDetails) details = e.response;
      if (
        e.response.data.error == "com.avispl.symphony.logout" ||
        e.response.data.error == "Connection Error" ||
        e.response.data.error ==
          "com.avispl.symphony.auth.common.error.SymphonyAuthenticationException"
      ) {
        if (e.response.data.error == "com.avispl.symphony.logout") {
          otherOptions.severity = "info";
        }
        cancelOthers = true;
      }
      // The APISIX gateway timing out sends back a generic HTML response, so need to fill out the message
      if (e.response.status === 504) {
        message = t("Request timed out");

        details = {
          ...e.response,
          data: {
            message: t(
              "Internal server error: the request took too long to process."
            ),
            errorCode: "",
            error: message,
          },
        };
      }
    } else if (axios.isAxiosError(e)) {
      message = e.name;
    } else {
      // Assume e is either a string, or an object with a proper toString function
      message = typeof e === "string" ? e : e.message || "";
    }

    dispatchNotification({
      title,
      message,
      details,
      ...otherOptions,
      cancelOthers: cancelOthers,
    });
  };

  return dispatchErrorNotification;
};
export default NotificationProvider;
